import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { CheckboxSegmented } from '@components/CheckboxSegmented';
import TimeZone from '@components/TimeZone';
import { Alert, Col, Form, Input, notification, Row, Select, Table, Tooltip } from 'antd';
import moment from 'moment';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import { ALLOWED_ROLES, getFullUrl, mergeAllowedRoles, useIsMobile } from '@src/packages/utils';
import ShiftService from '@src/services/ShiftService';
import IntlMessages from '@src/util/IntlMessages';

import ShiftPermission from '../../components/ShiftPermission';
import { createRecurrenceWeeksStartBreakRule } from '../../components/ActiveDay';
import {
  DEFAULT_DAYS_OPTIONS,
  KIND_OF_CYCLES,
  PATHS,
  SHIFT_STATUS,
  SHIFT_TYPES,
} from '../../constants';
import { useShiftValidation } from '../../hooks/useShiftValidation';
import { useShiftValidationTranslations } from '../../hooks/useShiftValidationTranslations';
import type {
  ActiveDaySerialized,
  ActiveDayValue,
  OrganizationsReduxState,
  WorkJourneySummary,
} from '../../types';
import styles from '../styles.module.less';
import {
  buildDynamicColumns,
  buildDynamicFooter,
  buildDynamicSource,
  buildFilteredDays,
  formatRegulationName,
} from '../utils';

const { Item, useForm } = Form;
/**
 * NOTE:
 * - recurrence = day of the week
 * - week = week number
 */
export default function ShiftCycle() {
  const { isMobile } = useIsMobile();
  const navigate = useHistory();
  const intl = useIntl();
  const [form] = useForm();
  const organizationId = useSelector(
    ({ organizations }: OrganizationsReduxState) => organizations?.organization?.id,
  );
  const { shiftId } = useParams<{ shiftId?: string }>();

  const [loading, setLoading] = React.useState(false);
  const [showContent, setShowContent] = React.useState(false);
  const [workJourneys, setWorkJourneys] = React.useState<WorkJourneySummary[]>([]);

  const shiftService = useMemo(() => new ShiftService(), []);
  const hasAssociatedJourneys = workJourneys.length > 0 && !!shiftId;

  const {
    hasWarnings,
    validateDaysRule,
    validateDays,
    validateSundaysRule,
    validateSunday,
  } = useShiftValidation(SHIFT_TYPES.CYCLE, {}, form);
  const validationMessages = useShiftValidationTranslations();

  const getWorkJourneysByShiftId = async (id: string, orgId: string) => {
    const journeys = await shiftService.getWorkJourneysByShiftId(id, orgId);
    setWorkJourneys(journeys);
    return journeys;
  };

  const onSave = async () => {
    try {
      setLoading(true);

      if (shiftId) {
        await shiftService.updateShiftCycle({
          ...form.getFieldsValue(),
          organizationId,
          status: SHIFT_STATUS.ACTIVE,
          id: shiftId,
        });
      } else {
        await shiftService.createShiftCycle({
          ...form.getFieldsValue(),
          organizationId,
          status: SHIFT_STATUS.ACTIVE,
        });
      }

      notification.success({
        message: intl.formatMessage({ id: 'general.save.successful.message' }),
      });
      navigate.push(getFullUrl(PATHS.BASE_URL, window.location.pathname));
    } catch (error) {
      notification.error({
        message: 'Error',
        description: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (
    newDayValues: ActiveDayValue,
    targetDate: { week: number; day: string | number },
  ) => {
    const { week: weekValue, day } = targetDate;
    const daysSelected = form.getFieldValue('days');

    const recurrenceData = form.getFieldValue('recurrence') || {
      weeks: [],
      totalShiftHours: 0,
    };

    const existingWeekIndex = recurrenceData.weeks.findIndex(w => w.week === weekValue);

    const updatedWeeks =
      existingWeekIndex === -1 // if week does not exist
        ? [
            ...recurrenceData.weeks,
            {
              week: weekValue,
              days: { [day]: newDayValues },
              totalHours: 0,
            },
          ]
        : recurrenceData.weeks.map((week, idx) => {
            if (idx !== existingWeekIndex) return week;

            const filteredDays = buildFilteredDays(week.days, daysSelected);
            return {
              ...week,
              days: {
                ...filteredDays,
                [day]: newDayValues,
              },
            };
          });

    // Calculate totalHours for each week and totalShiftHours
    const updatedWeeksWithHours = updatedWeeks.map(week => {
      const weekHours = ShiftService.calculateTotalShiftHoursDynamicDays(week);
      return {
        ...week,
        totalHours: weekHours.minutes,
      };
    });

    const totalShiftHours = updatedWeeksWithHours.reduce((acc, week) => acc + week.totalHours, 0);

    const shiftConfig = {
      ...recurrenceData,
      weeks: updatedWeeksWithHours,
      totalShiftHours,
    };

    form.setFieldValue('recurrence', shiftConfig);

    // Build regulation name
    const regulationName = formatRegulationName(shiftConfig);
    form.setFieldValue('regulationName', regulationName);
  };

  const resetRecurrence = () => {
    form.resetFields(['recurrence']);
  };

  React.useEffect(() => {
    if (shiftId) {
      const fetchShift = async () => {
        const shift = await shiftService.getShift(shiftId, organizationId);
        if (!shift) return;

        const recurrenceData = {
          totalShiftHours: shift.totalShiftHours,
          weeks: shift.recurrence.weeks.map(week => ({
            week: week.week,
            days: Object.entries(week.days).reduce(
              (acc, [dayKey, dayValue]) => {
                const day = dayValue as ActiveDaySerialized;
                acc[dayKey] = {
                  ...day,
                  startTime: day.startTime
                    ? moment(day.startTime, 'HH:mm')
                    : moment('08:00', 'HH:mm'),
                  endTime: day.endTime ? moment(day.endTime, 'HH:mm') : moment('08:00', 'HH:mm'),
                  startBreakTime: day.startBreakTime
                    ? moment(day.startBreakTime, 'HH:mm')
                    : null,
                };
                return acc;
              },
              {} as Record<string, ActiveDayValue>,
            ),
            totalHours: week.totalHours,
          })),
        };

        form.setFieldsValue({
          weekCount: shift.weekCount,
          days: shift.daysOfWeek,
          recurrence: recurrenceData,
          name: shift.name,
          regulationName: shift.regulationName,
          timeZone: shift.timeZone,
          resolution: shift.resolution
            ? {
                name: shift.resolution.name,
                date:
                  shift.resolution.date &&
                  typeof shift.resolution.date === 'object' &&
                  'seconds' in shift.resolution.date
                    ? moment.unix(shift.resolution.date.seconds)
                    : null,
              }
            : null,
        });
        validateSunday(shift.daysOfWeek || []);
        validateDays(shift.daysOfWeek || []);
        setShowContent(true);
      };
      fetchShift();
      getWorkJourneysByShiftId(shiftId, organizationId);
    } else {
      form.setFieldsValue({
        weekCount: null,
        recurrence: {
          totalShiftHours: 0,
          weeks: [
            { week: 1, days: {}, totalHours: 0 },
            { week: 2, days: {}, totalHours: 0 },
            { week: 3, days: {}, totalHours: 0 },
            { week: 4, days: {}, totalHours: 0 },
          ],
        },
        name: '',
        regulationName: '',
        timeZone: '',
        resolution: null,
      });
    }
  }, [form, organizationId, shiftId, shiftService]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BoxContainer>
      <BoxContainer content fixed bottomBorder>
        <FilterContainer
          goBack={() => navigate.goBack()}
          title={<Title.Header value={<IntlMessages id="shifts.create.cycle.title" />} />}
          buttonItems={[
            {
              iconName: 'save',
              action: () => form.submit(),
              disabled: loading,
              allowedRole: mergeAllowedRoles(
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.CREATE,
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.UPDATE,
              ),
            },
          ]}
        />
      </BoxContainer>
      <BoxContainer content loading={loading}>
        <Form
          form={form}
          name="shiftCycle"
          layout={FORM_LAYOUT_DEFAULT as unknown as React.ComponentProps<typeof Form>['layout']}
          onFinish={onSave}
          initialValues={{
            weekCount: null,
            days: [],
            recurrence: {
              totalShiftHours: 0,
              weeks: [],
            },
            name: '',
            regulationName: '',
          }}
        >
          {hasAssociatedJourneys && (
            <Row>
              <Alert
                message={intl.formatMessage({ id: 'shifts.edit.hasAssociatedJourneys' })}
                type="warning"
                showIcon
              />
            </Row>
          )}
          <Row gutter={[8, 8]}>
            <Col xs={24} md={12} lg={12}>
              <Title.SubTitle value={<IntlMessages id="shifts.configuration" />} />
              <Title.Description value={<IntlMessages id="shifts.cycle.configuration.details" />} />
            </Col>
            <Col span={24}>
              <Title.LabelForm
                className="gx-mb-2"
                required
                value={<IntlMessages id="shifts.cycle.type" />}
              />
              <Item
                name="weekCount"
                rules={[
                  { required: true, message: validationMessages.weekCountRequired },
                ]}
              >
                <Select
                  disabled={hasAssociatedJourneys}
                  onChange={e => setShowContent(e !== null)}
                  className="gx-w-100"
                  options={KIND_OF_CYCLES}
                />
              </Item>
            </Col>
            <Item noStyle hidden={!showContent}>
              <Col span={24}>
                <Title.LabelForm
                  className="gx-mb-2"
                  value={<IntlMessages id="shifts.recurrence" />}
                />
                <Item
                  name="days"
                  rules={[
                    {
                      required: true,
                      message: validationMessages.daysRequired,
                    },
                    validateDaysRule(),
                    validateSundaysRule(),
                  ]}
                >
                  <CheckboxSegmented
                    disabled={hasAssociatedJourneys}
                    onChange={values => {
                      !values.length && resetRecurrence();
                      validateDays(values);
                    }}
                    options={DEFAULT_DAYS_OPTIONS}
                  />
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  noStyle
                  shouldUpdate={(prev, next) =>
                    JSON.stringify(prev.recurrence) !== JSON.stringify(next.recurrence) ||
                    JSON.stringify(prev.days) !== JSON.stringify(next.days) ||
                    prev.weekCount !== next.weekCount
                  }
                >
                  {({ getFieldValue }) => (
                    <Item name="recurrence" rules={[createRecurrenceWeeksStartBreakRule(intl)]}>
                      <Table
                        bordered
                        className={styles.shiftTable}
                        columns={buildDynamicColumns(getFieldValue('days'))}
                        dataSource={buildDynamicSource({
                          getFieldValue,
                          onChange: handleTableChange,
                          disabled: hasAssociatedJourneys,
                        })}
                        pagination={false}
                        footer={() => (
                          <div className="gx-flex-row gx-guarnic-gap-2">
                            <div className="gx-font-weight-bold">
                              <IntlMessages id="shifts.weeks.hours.estimated" />
                            </div>
                            <div>{buildDynamicFooter(getFieldValue('recurrence')).formated}</div>
                          </div>
                        )}
                        scroll={isMobile ? { x: 600 } : undefined}
                      />
                    </Item>
                  )}
                </Item>
              </Col>
              <Col xs={24} md={12} lg={12} xl={12}>
                <Title.LabelForm
                  className="gx-mb-2"
                  required
                  value={<IntlMessages id="shifts.form.timezone" />}
                />
                <Item
                  name="timeZone"
                  rules={[
                    { required: true, message: validationMessages.timeZoneRequired },
                  ]}
                >
                  <TimeZone className="gx-w-100" disabled={hasAssociatedJourneys} />
                </Item>
              </Col>
            </Item>
          </Row>
          <Item noStyle hidden={!showContent}>
            <Row gutter={[8, 8]}>
              <Col xs={24} md={12} lg={12} xl={12}>
                <Title.LabelForm
                  className="gx-mb-2"
                  required
                  value={<IntlMessages id="shifts.form.name" />}
                />
                <Item
                  name="name"
                  rules={[
                    { required: true, message: validationMessages.nameRequired },
                  ]}
                >
                  <Input />
                </Item>
              </Col>
              <Col xs={24} md={12} lg={12} xl={12}>
                <Title.LabelForm
                  className="gx-mb-2"
                  value={<IntlMessages id="shifts.form.regulation.name" />}
                />
                <Item noStyle shouldUpdate={(prev, next) => prev.recurrence !== next.recurrence}>
                  {({ getFieldValue }) => (
                    <Tooltip title={<div>{getFieldValue('regulationName')}</div>}>
                      <Item name="regulationName">
                        <Input disabled />
                      </Item>
                    </Tooltip>
                  )}
                </Item>
              </Col>
            </Row>
          </Item>
          <ShiftPermission
            show={
              hasWarnings() ||
              !!form.getFieldValue('resolution')?.name ||
              !!form.getFieldValue('resolution')?.date
            }
          />
        </Form>
      </BoxContainer>
    </BoxContainer>
  );
}
